import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { getPays } from '../api';

let _paysCache = null;
const loadPays = () => {
  if (!_paysCache) {
    _paysCache = getPays().then(r => r.data || []).catch(() => []);
  }
  return _paysCache;
};

const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export const PaysFlag = ({ code, size = 18 }) => {
  const [err, setErr] = useState(false);
  if (!code) return null;
  if (err) {
    return <span className="font-mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>{code}</span>;
  }
  return (
    <img
      src={`https://flagcdn.com/w40/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w80/${code.toLowerCase()}.png 2x`}
      alt={code}
      loading="lazy"
      onError={() => setErr(true)}
      style={{ width: size, height: 'auto', borderRadius: 2, flexShrink: 0, display: 'block' }}
    />
  );
};

export const PaysSelect = ({ value, onChange, className, style, testid = 'pays-select' }) => {
  const [pays, setPays] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => { loadPays().then(setPays); }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => searchRef.current?.focus(), 30);
    }
  }, [open]);

  const selected = useMemo(() => pays.find(p => p.code === value), [pays, value]);

  const filtered = useMemo(() => {
    const nq = norm(q);
    if (!nq) return pays;
    return pays.filter(p => norm(p.nom).includes(nq) || norm(p.code).includes(nq));
  }, [pays, q]);

  const pick = (code) => {
    onChange(code);
    setOpen(false);
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', flex: 1, minWidth: 0 }} data-testid={testid}>
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(o => !o)}
        className={className}
        style={{ ...style, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', textAlign: 'left', width: '100%' }}
      >
        <PaysFlag code={value} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.nom : (value || 'Sélectionner…')}
        </span>
        <ChevronDown size={13} style={{ flexShrink: 0, opacity: 0.5 }} />
      </button>

      {open && (
        <div
          data-testid={`${testid}-dropdown`}
          className="rounded-lg border shadow-lg"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 60,
            background: 'var(--surface, #fff)', borderColor: 'var(--border, #E5E7EB)',
            maxHeight: 260, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div style={{ padding: 6, borderBottom: '1px solid var(--border, #E5E7EB)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={13} style={{ opacity: 0.5, flexShrink: 0 }} />
            <input
              ref={searchRef}
              data-testid={`${testid}-search`}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Nom ou code (ex : Belgique, BE)…"
              className="text-xs"
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)' }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(p => (
              <button
                key={p.code}
                type="button"
                data-testid={`${testid}-option-${p.code.toLowerCase()}`}
                onClick={() => pick(p.code)}
                className="text-xs"
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '6px 10px', border: 'none', cursor: 'pointer', textAlign: 'left',
                  background: p.code === value ? 'var(--surface-hover, #F3F4F6)' : 'transparent',
                  color: 'var(--text)',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover, #F3F4F6)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = p.code === value ? 'var(--surface-hover, #F3F4F6)' : 'transparent'; }}
              >
                <PaysFlag code={p.code} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nom}</span>
                <span className="font-mono" style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.code}</span>
                {p.code === value && <Check size={12} style={{ color: 'var(--accent, #2563EB)', flexShrink: 0 }} />}
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-xs text-center py-3" style={{ color: 'var(--text-muted)' }} data-testid={`${testid}-empty`}>
                Aucun pays trouvé
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PaysSelect;
